var COUNTRY_SELECTOR_ROOT_SELECTOR = '.dropdown.country-selector[role="combobox"], .mobile-menu-dropdown.country-selector';
var COUNTRY_SELECTOR_OPTION_SELECTOR = '.dropdown-country-selector [role="option"]';

/**
 * Returns the element that exposes combobox semantics.
 *
 * @param {jQuery} $selector - Country selector root element
 * @returns {jQuery} - Combobox element
 */
function getCountryComboboxElement($selector) {
    if ($selector.is('[role="combobox"]')) {
        return $selector;
    }

    var $combobox = $selector.find('[role="combobox"]').first();
    return $combobox.length ? $combobox : $selector;
}

/**
 * Returns the currently active option based on aria-activedescendant.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {jQuery} - Active option element or empty jQuery object
 */
function getActiveCountryOption($selector, $countryOptions) {
    var activeOptionId = getCountryComboboxElement($selector).attr('aria-activedescendant');
    if (!activeOptionId) {
        return $();
    }

    return $countryOptions.filter('#' + activeOptionId);
}

/**
 * Returns the index of the currently active option.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {number} - Active option index or -1 when none is active
 */
function getActiveCountryOptionIndex($selector, $countryOptions) {
    return $countryOptions.index(getActiveCountryOption($selector, $countryOptions));
}

/**
 * Returns whether selector popup is currently open.
 *
 * @param {jQuery} $selector - Country selector root element
 * @returns {boolean} - Whether selector is open
 */
function isCountrySelectorOpen($selector) {
    return $selector.hasClass('show') || $selector.children('.dropdown-menu').hasClass('show');
}

/**
 * Returns all option elements inside selector.
 *
 * @param {jQuery} $selector - Country selector root element
 * @returns {jQuery} - Country option elements
 */
function getCountrySelectorOptions($selector) {
    return $selector.find(COUNTRY_SELECTOR_OPTION_SELECTOR);
}

/**
 * Returns first visible option (fallback to first option when visibility cannot be determined).
 *
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {jQuery} - First option
 */
function getFirstCountryOption($countryOptions) {
    var $visibleOptions = $countryOptions.filter(':visible');
    return $visibleOptions.length ? $visibleOptions.first() : $countryOptions.first();
}

/**
 * Returns last visible option (fallback to last option when visibility cannot be determined).
 *
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {jQuery} - Last option
 */
function getLastCountryOption($countryOptions) {
    var $visibleOptions = $countryOptions.filter(':visible');
    return $visibleOptions.length ? $visibleOptions.last() : $countryOptions.last();
}

/**
 * Returns index of currently focused option.
 *
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {number} - Focused option index or -1 when focus is not on an option
 */
function getFocusedCountryOptionIndex($countryOptions) {
    var $focusedOption = $(document.activeElement).closest('[role="option"]');
    return $countryOptions.index($focusedOption);
}

/**
 * Returns index to use for option navigation.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $countryOptions - Country option elements
 * @returns {number} - Current option index or -1 when none is active/focused
 */
function getCurrentCountryOptionIndex($selector, $countryOptions) {
    var focusedOptionIndex = getFocusedCountryOptionIndex($countryOptions);
    if (focusedOptionIndex !== -1) {
        return focusedOptionIndex;
    }

    return getActiveCountryOptionIndex($selector, $countryOptions);
}

/**
 * Safely prevents default browser behavior for keyboard events.
 *
 * @param {Event} e - Keyboard event
 */
function preventDefault(e) {
    if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
    }
}

/**
 * Syncs combobox aria-expanded from real popup visibility.
 *
 * @param {jQuery} $selector - Country selector combobox container
 */
function syncCountrySelectorExpandedState($selector) {
    var isOpen = isCountrySelectorOpen($selector);
    getCountryComboboxElement($selector).attr('aria-expanded', isOpen.toString());
}

/**
 * Updates combobox/listbox visual + ARIA expanded state.
 *
 * @param {jQuery} $selector - Country selector combobox container
 * @param {boolean} isOpen - Whether popup should be open
 */
function setCountrySelectorState($selector, isOpen) {
    $selector.toggleClass('show', isOpen);
    $selector.children('.dropdown-menu').toggleClass('show', isOpen);
    syncCountrySelectorExpandedState($selector);

    if (!isOpen) {
        getCountryComboboxElement($selector).removeAttr('aria-activedescendant');
        getCountrySelectorOptions($selector).attr('aria-selected', 'false').attr('tabindex', '-1');
    }
}

/**
 * Sets active option for combobox/listbox relationship.
 *
 * @param {jQuery} $selector - Country selector combobox container
 * @param {jQuery} $option - Active option element
 */
function setActiveCountryOption($selector, $option) {
    if (!$option || !$option.length) {
        return;
    }

    var $combobox = getCountryComboboxElement($selector);
    var optionId = $option.attr('id');
    if (optionId) {
        $combobox.attr('aria-activedescendant', optionId);
    }

    getCountrySelectorOptions($selector).attr('aria-selected', 'false').attr('tabindex', '-1');
    $option.attr('aria-selected', 'true').attr('tabindex', '0');
}

/**
 * Moves focus to an option and syncs ARIA selection metadata.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $option - Option element to focus
 */
function focusCountryOption($selector, $option) {
    if (!$option || !$option.length) {
        return;
    }

    setActiveCountryOption($selector, $option);

    // Keep DOM focus on combobox while active option is exposed through aria-activedescendant.
    getCountryComboboxElement($selector).focus();
}

/**
 * Opens selector and places focus inside the options list.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $countryOptions - Country option elements
 */
function openCountrySelector($selector, $countryOptions) {
    setCountrySelectorState($selector, true);

    if (!$countryOptions.length) {
        return;
    }

    var $activeOption = getActiveCountryOption($selector, $countryOptions);
    if (!$activeOption.length) {
        $activeOption = getFirstCountryOption($countryOptions);
    }

    focusCountryOption($selector, $activeOption);
}

/**
 * Restricts Enter/Space handling to combobox or option elements.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {Event} e - Keyboard event
 * @returns {boolean} - Whether selector keyboard shortcuts should run
 */
function shouldHandleCountrySelectorKey($selector, e) {
    if (!e || !e.target) {
        return true;
    }

    var $target = $(e.target);
    if ($target.closest('[role="option"]').length) {
        return true;
    }

    var $combobox = getCountryComboboxElement($selector);
    return $combobox.length && ($target.is($combobox) || $target.closest($combobox[0]).length > 0);
}

/**
 * Handles supported keyboard interactions for the country selector.
 *
 * @param {jQuery} $selector - Country selector root element
 * @param {jQuery} $countryOptions - Country option elements
 * @param {number} keyCode - Key code
 * @param {Event} e - Keyboard event
 */
function handleCountrySelectorKeydown($selector, $countryOptions, keyCode, e) {
    if (keyCode === 40) { // down
        preventDefault(e);

        if (!$countryOptions.length) {
            return;
        }

        if (!isCountrySelectorOpen($selector)) {
            openCountrySelector($selector, $countryOptions);
            return;
        }

        var currentIndexDown = getCurrentCountryOptionIndex($selector, $countryOptions);
        var nextIndex = currentIndexDown < 0 ? 0 : Math.min(currentIndexDown + 1, $countryOptions.length - 1);
        focusCountryOption($selector, $countryOptions.eq(nextIndex));
        return;
    }

    if (keyCode === 38) { // up
        preventDefault(e);

        if (!$countryOptions.length) {
            return;
        }

        if (!isCountrySelectorOpen($selector)) {
            openCountrySelector($selector, $countryOptions);
            focusCountryOption($selector, getLastCountryOption($countryOptions));
            return;
        }

        var currentIndexUp = getCurrentCountryOptionIndex($selector, $countryOptions);
        var prevIndex = currentIndexUp <= 0 ? 0 : currentIndexUp - 1;
        focusCountryOption($selector, $countryOptions.eq(prevIndex));
        return;
    }

    if (keyCode === 27) { // escape
        preventDefault(e);
        setCountrySelectorState($selector, false);
        getCountryComboboxElement($selector).focus();
        return;
    }

    if (keyCode === 9) { // tab
        setCountrySelectorState($selector, false);
        return;
    }

    if (keyCode === 13 || keyCode === 32) { // enter / space
        var isOpen = isCountrySelectorOpen($selector);
        preventDefault(e);

        if (!isOpen) {
            openCountrySelector($selector, $countryOptions);
            return;
        }

        var $activeOption = $(document.activeElement).closest('[role="option"]');
        if (!$activeOption.length) {
            $activeOption = getActiveCountryOption($selector, $countryOptions);
        }

        if (!$activeOption.length && $countryOptions.length) {
            $activeOption = getFirstCountryOption($countryOptions);
            setActiveCountryOption($selector, $activeOption);
        }

        if ($activeOption.length) {
            $activeOption.trigger('click');
        }
    }
}

module.exports = function () {
    $(COUNTRY_SELECTOR_ROOT_SELECTOR).each(function () {
        var $selector = $(this);
        getCountrySelectorOptions($selector).attr('tabindex', '-1');
        syncCountrySelectorExpandedState($selector);
    });

    $(COUNTRY_SELECTOR_ROOT_SELECTOR).on('shown.bs.dropdown hidden.bs.dropdown', function () {
        syncCountrySelectorExpandedState($(this));
    });

    // Handle click on toggle button to update aria-expanded immediately
    $(document).on('click', '.country-selector .dropdown-toggle', function () {
        var $selector = $(this).closest('.country-selector');
        // Delay to let Bootstrap apply the show class first
        setTimeout(function () {
            syncCountrySelectorExpandedState($selector);
        }, 0);
    });

    $('.country-selector a').click(function (e) {
        e.preventDefault();
        var $selector = $(this).closest('.country-selector');

        setActiveCountryOption($selector, $(this));
        setCountrySelectorState($selector, false);
        var action = $('.page').data('action');
        var localeCode = $(this).data('locale');
        var localeCurrencyCode = $(this).data('currencycode');
        var queryString = $('.page').data('querystring');
        var url = $selector.data('url');
        var params = new URLSearchParams(queryString);
        var queryaction = params.get('action');
        if ($('.login-form-nav .nav-tabs').length > 0) {
            var actionControl = $('.login-form-nav .nav-tabs').find('.nav-link.active').attr('aria-controls');
            if (actionControl) {
                if (queryString){
                    queryString = queryaction ? queryString.replace(/action=[^&]*/g, 'action=' + actionControl) : (queryString + '&action=' + actionControl);
                } else {
                    queryString = 'action=' + actionControl;
                }
            }
        }
        $.ajax({
            url: url,
            type: 'get',
            dataType: 'json',
            data: {
                code: localeCode,
                queryString: queryString,
                CurrencyCode: localeCurrencyCode,
                action: action
            },
            success: function (response) {
                $.spinner().stop();
                if (response && response.redirectUrl) {
                    window.location.href = response.redirectUrl;
                }
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });

    $(document)
        .off('keydown.countrySelector', COUNTRY_SELECTOR_ROOT_SELECTOR + ', ' + COUNTRY_SELECTOR_OPTION_SELECTOR)
        .on('keydown.countrySelector', COUNTRY_SELECTOR_ROOT_SELECTOR + ', ' + COUNTRY_SELECTOR_OPTION_SELECTOR, function (e) {
            var keyCode = e.which || e.keyCode;
            if ([9, 13, 27, 32, 38, 40].indexOf(keyCode) === -1) {
                return;
            }

            var $target = $(e.target);
            var $selector = $target.is(COUNTRY_SELECTOR_ROOT_SELECTOR)
                ? $target
                : $target.closest(COUNTRY_SELECTOR_ROOT_SELECTOR);

            if (!$selector.length) {
                $selector = $target.closest('.country-selector');
            }

            if (!$selector.length || !shouldHandleCountrySelectorKey($selector, e)) {
                return;
            }

            handleCountrySelectorKeydown($selector, getCountrySelectorOptions($selector), keyCode, e);
        });
};
